import React, { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import {
  Avatar,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  FormControl,
  TextField,
  Chip,
  InputAdornment,
  Stack,
  Tooltip,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Add,
  Search,
  Visibility,
  VisibilityOff,
  PhotoCameraOutlined,
  DeleteOutline,
  Close,
  ManageAccounts,
  LockReset,
  AlternateEmail,
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { DefaultPage } from "../../layouts/default-page";
import { Notify } from "../../layouts/notify";
import { SignUp } from "../../services/AuthService";
import {
  useAddMTRCBUserMutation,
  useGetMTRCBUsersQuery,
  useLazyGetMTRCBUsersQuery,
  useUpdateMTRCBUserMutation,
  useChangeDeputyPasswordMutation,
  useChangeUserEmailMutation,
} from "../../services/user.query";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../common/StatusChip";
import { RowActions } from "../common/RowActions";
import { ExportLoadingBackdrop } from "../common/ExportLoadingBackdrop";

type FormValues = {
  email: string;
  password: string;
  phone_number: string;
  name: string;
  last_name: string;
  sub_role: string;
  emp_id: string;
  emp_position: string;
  card_issued_date: string;
  status: string;

  profile_picture: string | null;
  profile_pic_file: File | null;

  id_url: string | null;
  id_image_file: File | null;
};

type PasswordFormValues = {
  new_password: string;
  confirm_password: string;
};

const ROLE_OPTIONS = ["MTRCB", "AMITY", "LEGACY"];

const getValidationSchema = (isEdit: boolean) =>
  Yup.object({
    name: Yup.string().required("Firstname is required"),
    last_name: Yup.string().required("Lastname is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: isEdit
      ? Yup.string().notRequired()
      : Yup.string()
        .min(8, "Password must be at least 8 characters long")
        .required("Password is required"),
    phone_number: Yup.string()
      .nullable()
      .test(
        "digits-only",
        "Phone number must be only digits",
        (value) => !value || /^[0-9]+$/.test(value),
      ),
    sub_role: Yup.string().required("Role is required"),
    emp_id: Yup.string().required("Employee ID is required"),
    emp_position: Yup.string().required("Employee Position is required"),
    card_issued_date: Yup.string().required("Card Issued Date is required"),
    status: Yup.string().required("Status is required"),

    profile_picture: Yup.string().nullable(),
    profile_pic_file: (isEdit
      ? Yup.mixed<File>().nullable()
      : Yup.mixed<File>().nullable().required("Profile picture is required")
    )
      .test(
        "fileType",
        "Only PNG, JPG, JPEG, and WEBP are allowed",
        (value) =>
          !value ||
          ["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(
            value.type,
          ),
      )
      .test(
        "fileSize",
        "Image must not exceed 2MB",
        (value) => !value || value.size <= 2 * 1024 * 1024,
      ),

    id_url: Yup.string().nullable(),
    id_image_file: (isEdit
      ? Yup.mixed<File>().nullable()
      : Yup.mixed<File>().nullable()
    )
      .test(
        "fileType",
        "Only PNG, JPG, JPEG, and WEBP are allowed",
        (value) =>
          !value ||
          ["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(
            value.type,
          ),
      )
      .test(
        "fileSize",
        "Image must not exceed 2MB",
        (value) => !value || value.size <= 2 * 1024 * 1024,
      ),
  });

const formatDateInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const safeImageSrc = (value?: string | null) => {
  if (!value) return "";
  if (value.startsWith("blob:")) return "";
  return value;
};

export default function MTRCBUsersPage() {
  const [parentPage, setParentPage] = useState(1);
  const rowsPerPage = 100;

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<
    "EMPLOYEE_ID" | "LAST_NAME" | "CREATED_AT"
  >("CREATED_AT");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedRole, setSelectedRole] = useState<
    "ALL" | "MTRCB" | "AMITY" | "LEGACY"
  >("ALL");

  const [openAddModal, setOpenAddModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [selectedActionUser, setSelectedActionUser] = useState<any>(null);

  const [openChangePasswordDialog, setOpenChangePasswordDialog] =
    useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [openChangeEmailDialog, setOpenChangeEmailDialog] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");

  const [picPreviewUrl, setPicPreviewUrl] = useState("");
  const [idPreviewUrl, setIdPreviewUrl] = useState("");

  const [picDragOver, setPicDragOver] = useState(false);
  const [idDragOver, setIdDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const idFileInputRef = useRef<HTMLInputElement | null>(null);

  const { data, isLoading, isFetching, refetch } = useGetMTRCBUsersQuery({
    page: parentPage,
    rowsPerPage,
    searchName: searchTerm,
    searchEmployeeId: "",
    searchTerm,
    sortBy,
    sortDir,
    subRole: selectedRole === "ALL" ? undefined : selectedRole,
  });

  const [addMTRCBUser, { isLoading: addUserLoading }] =
    useAddMTRCBUserMutation();

  const [updateMTRCBUser, { isLoading: updateUserLoading }] =
    useUpdateMTRCBUserMutation();

  const [changeDeputyPassword, { isLoading: changePasswordLoading }] =
    useChangeDeputyPasswordMutation();

  const [changeUserEmail, { isLoading: changingEmail }] =
    useChangeUserEmailMutation();

  const users = data?.list ?? [];
  const count = data?.count ?? 0;

  const [triggerFetchAllMTRCBUsers] = useLazyGetMTRCBUsersQuery();
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const handleSearch = () => {
    setParentPage(1);
    setSearchTerm(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setParentPage(1);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching MTRCB personnel accounts...");
    try {
      const result = await triggerFetchAllMTRCBUsers({
        page: 1,
        rowsPerPage: 999999,
        searchName: searchTerm,
        searchEmployeeId: "",
        searchTerm,
        sortBy,
        sortDir,
        subRole: selectedRole === "ALL" ? undefined : selectedRole,
      }).unwrap();

      const allRows = result?.list || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Preparing PDF document for all ${allRows.length.toLocaleString()} MTRCB personnel...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const doc = new jsPDF("landscape", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Personnel & Staff Directory", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Role: ${selectedRole} | Total Personnel: ${allRows.length}`,
        14,
        22
      );

      const tableColumn = [
        "#",
        "Employee ID",
        "Full Name",
        "Email Address",
        "Position",
        "Role",
        "Status",
      ];

      const tableRows = allRows.map((item: any, idx: number) => [
        idx + 1,
        item.emp_id || "—",
        `${item.last_name || ""}, ${item.name || ""}`.trim() || "—",
        item.email || "—",
        item.position || "—",
        item.sub_role || item.role || "MTRCB",
        item.status || "VERIFIED",
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Personnel_${selectedRole}_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${allRows.length} personnel to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching MTRCB personnel accounts...");
    try {
      const result = await triggerFetchAllMTRCBUsers({
        page: 1,
        rowsPerPage: 999999,
        searchName: searchTerm,
        searchEmployeeId: "",
        searchTerm,
        sortBy,
        sortDir,
        subRole: selectedRole === "ALL" ? undefined : selectedRole,
      }).unwrap();

      const allRows = result?.list || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Generating Excel spreadsheet for all ${allRows.length.toLocaleString()} personnel...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const rows = allRows.map((item: any, idx: number) => ({
        "No.": idx + 1,
        "Employee ID": item.emp_id || "—",
        "Last Name": item.last_name || "—",
        "First Name": item.name || "—",
        "Email Address": item.email || "—",
        "Position": item.position || "—",
        "Role": item.sub_role || item.role || "MTRCB",
        "Status": item.status || "VERIFIED",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Personnel");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 16 },
        { wch: 20 },
        { wch: 20 },
        { wch: 32 },
        { wch: 24 },
        { wch: 16 },
        { wch: 14 },
      ];

      XLSX.writeFile(wb, `MTRCB_Personnel_${selectedRole}_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${allRows.length} personnel to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const openUserActionDialog = (user: any) => {
    setSelectedActionUser(user);
    setOpenActionDialog(true);
  };

  const closeUserActionDialog = () => {
    setOpenActionDialog(false);
    setSelectedActionUser(null);
  };

  const handleOpenUpdateFromAction = () => {
    const user = selectedActionUser;
    setOpenActionDialog(false);
    setSelectedActionUser(null);

    if (user) {
      openAddEditModal(user);
    }
  };

  const handleOpenChangePasswordFromAction = () => {
    setOpenActionDialog(false);
    setOpenChangePasswordDialog(true);
    passwordFormik.resetForm();
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleOpenChangeEmailFromAction = () => {
    setOpenActionDialog(false);
    setNewEmail("");
    setConfirmEmail("");
    setOpenChangeEmailDialog(true);
  };

  const closeChangeEmailDialog = () => {
    if (changingEmail) return;
    setOpenChangeEmailDialog(false);
    setNewEmail("");
    setConfirmEmail("");
  };

  const submitChangeEmail = async () => {
    try {
      if (!selectedActionUser?.user_id) {
        Notify("error", "Missing Supabase Auth user_id for this user.");
        return;
      }

      const emailTrimmed = newEmail.trim().toLowerCase();
      const confirmTrimmed = confirmEmail.trim().toLowerCase();

      if (!emailTrimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
        Notify("warning", "Please enter a valid email address.");
        return;
      }

      if (emailTrimmed !== confirmTrimmed) {
        Notify("warning", "Email confirmation does not match.");
        return;
      }

      if (emailTrimmed === (selectedActionUser?.email || "").trim().toLowerCase()) {
        Notify("warning", "The new email must be different from the current email.");
        return;
      }

      await changeUserEmail({
        user_id: selectedActionUser.user_id,
        new_email: emailTrimmed,
        users_id: selectedActionUser.id,
        is_deputy: false,
      }).unwrap();

      Notify("success", "Email changed successfully.");
      closeChangeEmailDialog();
      refetch();
    } catch (error: any) {
      console.error(error);
      Notify("error", error?.data || error?.message || "Failed to change email");
    }
  };

  const closeChangePasswordDialog = () => {
    setOpenChangePasswordDialog(false);
    setSelectedActionUser(null);
    passwordFormik.resetForm();
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const closeAddEditModal = () => {
    setOpenAddModal(false);
    setEditMode(false);
    setCurrentUser(null);

    setPicPreviewUrl("");
    setIdPreviewUrl("");

    formik.resetForm();

    if (fileInputRef.current) fileInputRef.current.value = "";
    if (idFileInputRef.current) idFileInputRef.current.value = "";
  };

  const openAddEditModal = (user: any = null) => {
    if (user) {
      setEditMode(true);
      setCurrentUser(user);

      const existingPicture = user?.profile_picture || "";
      const existingIdImage = user?.id_url || "";

      formik.setValues({
        email: user?.email || "",
        password: "",
        phone_number: user?.phone_number || "",
        name: user?.name || "",
        last_name: user?.last_name || "",
        sub_role: user?.sub_role || "",
        emp_id: user?.emp_id || "",
        emp_position: user?.emp_position || "",
        card_issued_date: formatDateInput(user?.card_issued_date),
        status: user?.status || "VERIFIED",

        profile_picture: existingPicture || null,
        profile_pic_file: null,

        id_url: existingIdImage || null,
        id_image_file: null,
      });

      setPicPreviewUrl(existingPicture || "");
      setIdPreviewUrl(existingIdImage || "");
    } else {
      setEditMode(false);
      setCurrentUser(null);
      setPicPreviewUrl("");
      setIdPreviewUrl("");
      formik.resetForm();
    }

    setOpenAddModal(true);
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
      phone_number: "",
      name: "",
      last_name: "",
      sub_role: "",
      emp_id: "",
      emp_position: "",
      card_issued_date: "",
      status: "VERIFIED",

      profile_picture: null,
      profile_pic_file: null,

      id_url: null,
      id_image_file: null,
    },
    validationSchema: getValidationSchema(editMode),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (editMode) {
          await updateMTRCBUser({
            id: currentUser?.id,
            name: values.name,
            last_name: values.last_name,
            phone_number: values.phone_number,
            sub_role: values.sub_role,
            emp_id: values.emp_id,
            emp_position: values.emp_position,
            card_issued_date: values.card_issued_date,
            status: values.status,

            profile_picture: values.profile_picture,
            profile_pic_file: values.profile_pic_file,

            id_url: values.id_url,
            id_image_file: values.id_image_file,
          }).unwrap();

          Notify("success", "MTRCB User updated successfully");
        } else {
          const signUpResponse = await SignUp(values.email, values.password);

          if (!signUpResponse?.user?.id) {
            throw new Error("Failed to create auth account");
          }

          await addMTRCBUser({
            user_id: signUpResponse.user.id,
            email: values.email,
            phone_number: values.phone_number,
            name: values.name,
            last_name: values.last_name,
            role: "DEPUTY",
            sub_role: values.sub_role,
            status: values.status,
            emp_id: values.emp_id,
            emp_position: values.emp_position,
            card_issued_date: values.card_issued_date,

            profile_picture: values.profile_picture,
            profile_pic_file: values.profile_pic_file,

            id_url: values.id_url,
            id_image_file: values.id_image_file,
          }).unwrap();

          Notify("success", "MTRCB User added successfully");
        }

        closeAddEditModal();
        refetch();
      } catch (error: any) {
        console.error("MTRCB user save error:", error);
        Notify("error", error?.data || error?.message || "Failed to save user");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const passwordFormik = useFormik<PasswordFormValues>({
    initialValues: {
      new_password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      new_password: Yup.string()
        .min(8, "Password must be at least 8 characters long")
        .required("New password is required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("new_password")], "Passwords do not match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const authUserId = selectedActionUser?.user_id;

        if (!authUserId) {
          throw new Error("This MTRCB user has no Supabase Auth user_id.");
        }

        await changeDeputyPassword({
          user_id: authUserId,
          password: values.new_password,
        }).unwrap();

        Notify("success", "Password changed successfully");
        closeChangePasswordDialog();
        refetch();
      } catch (error: any) {
        console.error("MTRCB user change password error:", error);
        Notify(
          "error",
          error?.data || error?.message || "Failed to change password",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleSelectedImage = (file: File | null, type: "profile" | "id") => {
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    const maxSize = 2 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      Notify("error", "Only PNG, JPG, JPEG, and WEBP are allowed");
      return;
    }

    if (file.size > maxSize) {
      Notify("error", "Image must not exceed 2MB");
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    if (type === "profile") {
      setPicPreviewUrl(previewUrl);
      formik.setFieldValue("profile_pic_file", file);
    } else {
      setIdPreviewUrl(previewUrl);
      formik.setFieldValue("id_image_file", file);
    }
  };

  const handleImageChange =
    (type: "profile" | "id") =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        handleSelectedImage(file, type);
      };

  const handleDropImage =
    (type: "profile" | "id") => (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (type === "profile") {
        setPicDragOver(false);
      } else {
        setIdDragOver(false);
      }

      const file = event.dataTransfer.files?.[0] || null;
      handleSelectedImage(file, type);
    };

  const handleRemoveImage = (type: "profile" | "id") => {
    if (type === "profile") {
      setPicPreviewUrl("");
      formik.setFieldValue("profile_pic_file", null);
      formik.setFieldValue("profile_picture", null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      setIdPreviewUrl("");
      formik.setFieldValue("id_image_file", null);
      formik.setFieldValue("id_url", null);

      if (idFileInputRef.current) {
        idFileInputRef.current.value = "";
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "rowNumber",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const globalIndex = (parentPage - 1) * rowsPerPage + row.index + 1;

          return (
            <Typography>
              {globalIndex}
            </Typography>
          );
        },
      },
      {
        accessorKey: "emp_id",
        header: "EMPLOYEE ID",
        size: 50,
        Cell: ({ row }: any) => row.original?.emp_id || "-",
      },
      {
        accessorKey: "user",
        header: "USER",
        Cell: ({ row }: any) => {
          const fullName = `${row.original?.name || ""} ${row.original?.last_name || ""
            }`.trim();

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={safeImageSrc(row.original?.profile_picture)}
                alt={fullName}
                sx={{ width: 40, height: 40 }}
              >
                {row.original?.name?.[0] || "U"}
              </Avatar>

              <Box>
                <Typography fontWeight={700}>{fullName || "-"}</Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        accessorKey: "emp_position",
        header: "EMPLOYEE POSITION",
        Cell: ({ row }: any) => row.original?.emp_position || "-",
      },
      {
        accessorKey: "card_issued_date",
        header: "ID ISSUED DATE",
        Cell: ({ row }: any) =>
          row.original?.card_issued_date
            ? formatDateInput(row.original.card_issued_date)
            : "-",
      },
      {
        accessorKey: "status",
        header: "STATUS",
        size: 130,
        Cell: ({ row }: any) => {
          const status = row.original?.status || "-";
          const label = status === "VERIFIED" ? "ACTIVE" : status;
          return (
            <StatusChip
              label={label}
              tone={label === "ACTIVE" ? "success" : "neutral"}
            />
          );
        },
      },
      {
        id: "actions",
        header: "ACTIONS",
        size: 190,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const user = row.original;
          return (
            <RowActions
              actions={[
                {
                  label: "Edit",
                  onClick: () => openAddEditModal(user),
                },
                {
                  label: "Password",
                  onClick: () => {
                    setSelectedActionUser(user);
                    setOpenChangePasswordDialog(true);
                    passwordFormik.resetForm();
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  },
                },
                {
                  label: "Email",
                  onClick: () => {
                    setSelectedActionUser(user);
                    setNewEmail("");
                    setConfirmEmail("");
                    setOpenChangeEmailDialog(true);
                  },
                },
              ]}
              inline={2}
            />
          );
        },
      },
    ],
    [parentPage, rowsPerPage],
  );

  return (
    <DefaultPage 
      title="MTRCB Users" 
      subTitle="Manage MTRCB staff and administrator accounts."
      loading={isLoading || isFetching}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays registered MTRCB users. Set the filters and click <strong>Search</strong> to load results.
        </Alert>
      </Box>

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              select
              size="small"
              label="Role"
              value={selectedRole}
              onChange={(e) => {
                setParentPage(1);
                setSelectedRole(e.target.value as "ALL" | "MTRCB" | "AMITY" | "LEGACY");
              }}
              sx={{
                minWidth: 140,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="ALL">All Roles</MenuItem>
              <MenuItem value="MTRCB">MTRCB</MenuItem>
              <MenuItem value="AMITY">AMITY</MenuItem>
              <MenuItem value="LEGACY">LEGACY</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Sort by"
              value={sortBy}
              onChange={(e) => {
                setParentPage(1);
                setSortBy(
                  e.target.value as
                  | "EMPLOYEE_ID"
                  | "LAST_NAME"
                  | "CREATED_AT",
                );
              }}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="CREATED_AT">Latest Created</MenuItem>
              <MenuItem value="EMPLOYEE_ID">Employee ID</MenuItem>
              <MenuItem value="LAST_NAME">Last Name</MenuItem>
            </TextField>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setParentPage(1);
                setSortDir((d) => (d === "asc" ? "desc" : "asc"));
              }}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "4px",
                borderColor: "#CBD5E1",
                color: "#334155",
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
              }}
            >
              {sortDir === "asc" ? "Asc ↑" : "Desc ↓"}
            </Button>

            <TextField
              size="small"
              label="Search Name, Employee ID, Position"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={onSearchKeyDown}
              sx={{
                minWidth: 300,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {(searchTerm || searchInput) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={clearSearch}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={() => openAddEditModal()}
                sx={{
                  backgroundColor: "#00934C",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#007a3f",
                    boxShadow: "none",
                  },
                }}
              >
                Add MTRCB User
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(count || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />

      <DataTableFive
        data={users}
        total={count}
        page={parentPage}
        pageSize={rowsPerPage}
        onPageChange={setParentPage}
        columns={columns as any}
        getRowId={(row: any) => String(row.id)}
        isLoading={isLoading || isFetching}
        onRowClick={(row: any) => openAddEditModal(row.original)}
      />

      <Dialog
        open={openActionDialog}
        onClose={closeUserActionDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeUserActionDialog}
            sx={{ position: "absolute", top: 8, right: 8, p: 0 }}
          >
            <img alt="close icon" src="/x.png" />
          </IconButton>

          <Typography variant="h6" fontWeight={700}>
            Select Action
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedActionUser
              ? `${selectedActionUser.last_name ?? ""}, ${selectedActionUser.name ?? ""
              }`
              : "MTRCB User"}
          </Typography>
        </Box>

        <DialogContent dividers sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ManageAccounts />}
              onClick={handleOpenUpdateFromAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Update MTRCB User
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<LockReset />}
              onClick={handleOpenChangePasswordFromAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#0054D3",
                "&:hover": { bgcolor: "#003f9e" },
              }}
            >
              Change Password
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<AlternateEmail />}
              onClick={handleOpenChangeEmailFromAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#2e7d32",
                "&:hover": { bgcolor: "#1b5e20" },
              }}
            >
              Change Email
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openChangePasswordDialog}
        onClose={closeChangePasswordDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeChangePasswordDialog}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Close />
          </IconButton>

          <Typography variant="h6" fontWeight={700}>
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {`${selectedActionUser?.name || ""} ${selectedActionUser?.last_name || ""
              }`.trim() || "MTRCB User"}
          </Typography>
        </Box>

        <form noValidate onSubmit={passwordFormik.handleSubmit}>
          <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <label
                  htmlFor="new_password"
                  style={{ color: "#AFAFAF", marginBottom: 7 }}
                >
                  New Password
                </label>
                <TextField
                  name="new_password"
                  id="new_password"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordFormik.values.new_password}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  error={
                    passwordFormik.touched.new_password &&
                    Boolean(passwordFormik.errors.new_password)
                  }
                  helperText={
                    passwordFormik.touched.new_password &&
                    passwordFormik.errors.new_password
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={() => setShowNewPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <FormControl fullWidth>
                <label
                  htmlFor="confirm_password"
                  style={{ color: "#AFAFAF", marginBottom: 7 }}
                >
                  Confirm New Password
                </label>
                <TextField
                  name="confirm_password"
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordFormik.values.confirm_password}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  error={
                    passwordFormik.touched.confirm_password &&
                    Boolean(passwordFormik.errors.confirm_password)
                  }
                  helperText={
                    passwordFormik.touched.confirm_password &&
                    passwordFormik.errors.confirm_password
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={closeChangePasswordDialog}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Cancel
            </Button>

            <LoadingButton
              type="submit"
              loading={changePasswordLoading || passwordFormik.isSubmitting}
              variant="contained"
              sx={{ textTransform: "none", borderRadius: "10px", px: 3 }}
            >
              Save Password
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Change Email Dialog */}
      <Dialog
        open={openChangeEmailDialog}
        onClose={closeChangeEmailDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeChangeEmailDialog}
            disabled={changingEmail}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Close />
          </IconButton>

          <Typography variant="h6" fontWeight={700}>
            Change Email
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {`${selectedActionUser?.name || ""} ${selectedActionUser?.last_name || ""}`.trim() || "MTRCB User"}
          </Typography>
        </Box>

        <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
          <Stack spacing={2}>
            {selectedActionUser?.email && (
              <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Current Email
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedActionUser.email}
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="New Email Address"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={changingEmail}
              helperText="Enter the new email address"
            />
            <TextField
              fullWidth
              label="Confirm New Email Address"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              disabled={changingEmail}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitChangeEmail();
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={closeChangeEmailDialog}
            disabled={changingEmail}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={changingEmail}
            variant="contained"
            onClick={submitChangeEmail}
            sx={{ textTransform: "none", borderRadius: "10px", px: 3, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}
          >
            Save Email
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddModal}
        onClose={closeAddEditModal}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "92vh",
          },
        }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeAddEditModal}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Close />
          </IconButton>

          <Typography variant="h6" fontWeight={700}>
            {editMode ? "Edit MTRCB User" : "Add MTRCB User"}
          </Typography>
        </Box>

        <form noValidate onSubmit={formik.handleSubmit}>
          <DialogContent
            dividers
            sx={{
              pt: 1,
              pb: 2,
              px: 2.5,
              maxHeight: "68vh",
              overflowY: "auto",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="name"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Firstname
                  </label>
                  <TextField
                    name="name"
                    id="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={
                      formik.touched.name && (formik.errors.name as any)
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="last_name"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Lastname
                  </label>
                  <TextField
                    name="last_name"
                    id="last_name"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.last_name &&
                      Boolean(formik.errors.last_name)
                    }
                    helperText={
                      formik.touched.last_name &&
                      (formik.errors.last_name as any)
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="email"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Email
                  </label>
                  <TextField
                    name="email"
                    id="email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={editMode}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={
                      formik.touched.email && (formik.errors.email as any)
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="phone_number"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Phone Number
                  </label>
                  <TextField
                    name="phone_number"
                    id="phone_number"
                    value={formik.values.phone_number}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.phone_number &&
                      Boolean(formik.errors.phone_number)
                    }
                    helperText={
                      formik.touched.phone_number &&
                      (formik.errors.phone_number as any)
                    }
                  />
                </FormControl>
              </Grid>

              {!editMode && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <label
                      htmlFor="password"
                      style={{ color: "#AFAFAF", marginBottom: 7 }}
                    >
                      Password
                    </label>
                    <TextField
                      name="password"
                      id="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      type={showPassword ? "text" : "password"}
                      error={
                        formik.touched.password &&
                        Boolean(formik.errors.password)
                      }
                      helperText={
                        formik.touched.password &&
                        (formik.errors.password as any)
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="sub_role"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Role
                  </label>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    name="sub_role"
                    id="sub_role"
                    value={formik.values.sub_role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.sub_role && Boolean(formik.errors.sub_role)
                    }
                    helperText={
                      formik.touched.sub_role && (formik.errors.sub_role as any)
                    }
                  >
                    <option value="" />
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </TextField>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="emp_id"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Employee ID
                  </label>
                  <TextField
                    name="emp_id"
                    id="emp_id"
                    value={formik.values.emp_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.emp_id && Boolean(formik.errors.emp_id)
                    }
                    helperText={
                      formik.touched.emp_id && (formik.errors.emp_id as any)
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="emp_position"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Employee Position
                  </label>
                  <TextField
                    name="emp_position"
                    id="emp_position"
                    value={formik.values.emp_position}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.emp_position &&
                      Boolean(formik.errors.emp_position)
                    }
                    helperText={
                      formik.touched.emp_position &&
                      (formik.errors.emp_position as any)
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="card_issued_date"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Card Issued Date
                  </label>
                  <TextField
                    name="card_issued_date"
                    id="card_issued_date"
                    type="date"
                    value={formik.values.card_issued_date}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputLabelProps={{ shrink: true }}
                    error={
                      formik.touched.card_issued_date &&
                      Boolean(formik.errors.card_issued_date)
                    }
                    helperText={
                      formik.touched.card_issued_date &&
                      (formik.errors.card_issued_date as any)
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <label
                    htmlFor="status"
                    style={{ color: "#AFAFAF", marginBottom: 7 }}
                  >
                    Status
                  </label>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    name="status"
                    id="status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    helperText={
                      formik.touched.status && (formik.errors.status as any)
                    }
                  >
                    <option value="VERIFIED">Active</option>
                    <option value="BLOCKED">Inactive</option>
                  </TextField>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      sx={{
                        color: "#AFAFAF",
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      Profile Picture
                    </Typography>

                    <Box
                      onDragOver={(e) => {
                        e.preventDefault();
                        setPicDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setPicDragOver(false);
                      }}
                      onDrop={handleDropImage("profile")}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        width: "100%",
                        border: "1px dashed #D0D5DD",
                        borderRadius: "12px",
                        background: picDragOver ? "#F5F9FF" : "#FAFAFA",
                        minHeight: 220,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        cursor: "pointer",
                        transition: "0.2s ease",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleImageChange("profile")}
                      />

                      {picPreviewUrl ? (
                        <Box
                          sx={{
                            width: "100%",
                            minHeight: 220,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            p: 2,
                          }}
                        >
                          <Box
                            component="img"
                            src={picPreviewUrl}
                            alt="Profile Preview"
                            sx={{
                              maxHeight: 220,
                              maxWidth: "100%",
                              objectFit: "contain",
                              borderRadius: 1,
                            }}
                          />

                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage("profile");
                            }}
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              bgcolor: "#fff",
                              border: "1px solid #E0E0E0",
                              "&:hover": { bgcolor: "#f5f5f5" },
                            }}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <PhotoCameraOutlined
                            sx={{
                              fontSize: 40,
                              color: "#98A2B3",
                              mb: 1,
                            }}
                          />
                          <Typography fontWeight={700} color="#344054">
                            Upload Profile Picture
                          </Typography>
                          <Typography variant="body2" color="#667085" mt={0.5}>
                            Click or drag image here
                          </Typography>
                          <Typography variant="caption" color="#98A2B3" mt={1}>
                            PNG, JPG, JPEG, WEBP up to 2MB
                          </Typography>
                        </>
                      )}
                    </Box>

                    {!!formik.touched.profile_pic_file &&
                      !!formik.errors.profile_pic_file && (
                        <Typography
                          color="error"
                          variant="caption"
                          mt={1}
                          display="block"
                        >
                          {formik.errors.profile_pic_file as any}
                        </Typography>
                      )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      sx={{
                        color: "#AFAFAF",
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      ID Image
                    </Typography>

                    <Box
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIdDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIdDragOver(false);
                      }}
                      onDrop={handleDropImage("id")}
                      onClick={() => idFileInputRef.current?.click()}
                      sx={{
                        width: "100%",
                        border: "1px dashed #D0D5DD",
                        borderRadius: "12px",
                        background: idDragOver ? "#F5F9FF" : "#FAFAFA",
                        minHeight: 220,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        cursor: "pointer",
                        transition: "0.2s ease",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <input
                        ref={idFileInputRef}
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleImageChange("id")}
                      />

                      {idPreviewUrl ? (
                        <Box
                          sx={{
                            width: "100%",
                            minHeight: 220,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            p: 2,
                          }}
                        >
                          <Box
                            component="img"
                            src={idPreviewUrl}
                            alt="ID Preview"
                            sx={{
                              maxHeight: 220,
                              maxWidth: "100%",
                              objectFit: "contain",
                              borderRadius: 1,
                            }}
                          />

                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage("id");
                            }}
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              bgcolor: "#fff",
                              border: "1px solid #E0E0E0",
                              "&:hover": { bgcolor: "#f5f5f5" },
                            }}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <PhotoCameraOutlined
                            sx={{
                              fontSize: 40,
                              color: "#98A2B3",
                              mb: 1,
                            }}
                          />
                          <Typography fontWeight={700} color="#344054">
                            Upload ID Image
                          </Typography>
                          <Typography variant="body2" color="#667085" mt={0.5}>
                            Click or drag image here
                          </Typography>
                          <Typography variant="caption" color="#98A2B3" mt={1}>
                            PNG, JPG, JPEG, WEBP up to 2MB
                          </Typography>
                        </>
                      )}
                    </Box>

                    {!!formik.touched.id_image_file &&
                      !!formik.errors.id_image_file && (
                        <Typography
                          color="error"
                          variant="caption"
                          mt={1}
                          display="block"
                        >
                          {formik.errors.id_image_file as any}
                        </Typography>
                      )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              pt: 1.5,
              bgcolor: "background.paper",
              borderTop: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <LoadingButton
              type="submit"
              loading={
                addUserLoading || updateUserLoading || formik.isSubmitting
              }
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                px: 3,
              }}
            >
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </DefaultPage>
  );
}
